import { IQuestionSchema } from "@Totum/QuestionnaireBuilder/Domain/schemas";
import { IQuestion, IQuestionnaireDetails } from "@Totum/QuestionnaireBuilder/Domain/types";
import { FormInstance, notification } from "antd";
import { IDetailsFormFields } from "@Totum/QuestionnaireBuilder/Presentation//types";

// ** Validators ** //

/**
 * Checks if the form is valid
 * @param form The form instance to check
 */
export const isFormValid = async (form: FormInstance): Promise<boolean> => {
    return await form
        .validateFields()
        .then(() => true)
        .catch(() => false);
};

/**
 * Checks if the given question type is valid
 * @param questionType The question type to validate
 * @returns A type predicate indicating if the questionType is valid
 */
export const isValidQuestionType = (questionType: string): questionType is IQuestion["questionType"] => {
    const QuestionTypeSchema = IQuestionSchema.shape.questionType;
    return QuestionTypeSchema.safeParse(questionType).success;
};

// ** General ** //

/**
 * Resets the form and populates the selection set based on the questionnaire data
 * @param questionnaire The current questionnaire data
 * @param form The form instance to reset and populate
 * @param entitySelectionSetRef A ref to the set of selected entities
 * @param categorySets The sets of category and parent category IDs
 */
export const resetAndPopulateSelectionSet = (
    questionnaire: IQuestionnaireDetails,
    form: FormInstance<IDetailsFormFields>,
    entitySelectionSetRef: React.MutableRefObject<Set<string>>,
    categorySets: {
        categoryIds: Set<string>;
        parentCategoryIds: Set<string>;
    }
) => {
    form.resetFields();
    entitySelectionSetRef.current.clear();

    questionnaire.associationCollection.activities?.forEach((id) => {
        entitySelectionSetRef.current.add(id);
    });

    questionnaire.associationCollection.activityCategories?.forEach((id) => {
        if (!categorySets.categoryIds.has(id)) return;
        const prefix = categorySets.parentCategoryIds.has(id) ? "cat-" : "subcat-";
        entitySelectionSetRef.current.add(`${prefix}${id}`);
    });

    form.setFieldsValue({
        questionnaireId: questionnaire.questionnaireId,
        title: questionnaire.title,
        description: questionnaire.description,
        points: questionnaire.points,
        associationCollection: Array.from(entitySelectionSetRef.current),
    });
};

// ** General ** //

/**
 * Returns preset text options based on the question type
 * @param type The type of the question
 */
export const getQuestionPresetText = (type: string) => {
    switch (type) {
        case "boolean":
            return ["Yes", "No"];
        case "choice":
            return [];
        case "confidence":
            return [
                "Extremely confident",
                "Very confident",
                "Confident",
                "Somewhat confident",
                "I require additional training",
            ];
    }
    return null;
};

/**
 * Shows a notification based on the success of the questionnaire update
 * @param isSuccess The success status of the update
 * @param title The title of the questionnaire
 * @param actionVerb The action verb to display ie "update", "create" | "delete"
 */
export const showQuestionnaireUpdatedNotification = (isSuccess: boolean, title?: string, actionVerb = "update") => {
    if (isSuccess) {
        const titleText = title ? `"${title}" ` : "";
        notification.success({
            message: `The ${titleText}questionnaire was ${actionVerb}d successfully.`,
        });
    } else {
        notification.error({
            message: `Failed to ${actionVerb} the questionnaire.`,
        });
    }
};

/**
 * Shows a notification based on the success of the questionnaire update
 * @param isSuccess The success status of the update
 * @param title The title of the questionn
 * @param actionVerb The action verb to display ie "update", "create" | "delete"
 */
export const showQuestionUpdatedNotification = (isSuccess: boolean, title?: string, actionVerb = "update") => {
    if (isSuccess) {
        const titleText = title ? `"${title}" ` : "";
        notification.success({
            message: `The ${titleText}question was ${actionVerb}d successfully.`,
        });
    } else {
        notification.error({
            message: `Failed to ${actionVerb} the questionnaire.`,
        });
    }
};
